import {canonicaliseMachineNumber} from '@/shared/lib/machine-number';
import {parsePairingCode} from '@/entities/enrollment/lib';

/**
 * What the scanner found: a machine, or a legacy enrollment code.
 *
 * Two shapes, because two generations of firmware are in the field at once. A
 * current terminal renders `{panel}/machines/SVFID-XXXX-XXXX-XXXX` — a number
 * burned into the board, valid forever, scannable tomorrow. A terminal on the
 * previous firmware still renders `{panel}/enroll/{code}`, a short-lived pairing
 * code that expires in minutes. The cabinet has to connect either one until the
 * last of the old ones is updated.
 */
export type ScannedDevice =
  | {kind: 'machine'; machineNumber: string}
  | {kind: 'enrollment'; pairingCode: string};

/**
 * Normalise whatever the adopt-device screen received.
 *
 * Accepts, in order of how sure we are:
 *   - `https://admin.sam-vision.uz/machines/SVFID-7K3M-9QP2-4XZ5` (or the bare
 *     `/machines/…` path) → a machine, in canonical spelling
 *   - `https://admin.sam-vision.uz/enroll/A7Y2MKJT` → a legacy pairing code
 *   - anything that parses as a machine number on its own → a machine, so an
 *     operator can read the number off the terminal's screen and type it
 *   - anything else → a pairing code, unchanged
 *
 * Returns null only for empty input. Every other string is *somebody's* idea of
 * an identifier, and the server is a better judge of it than a regex here.
 */
export function parseScannedDevice(raw: string): ScannedDevice | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const fromUrl = trimmed.match(/\/machines\/([^/?#\s]+)/i);
  if (fromUrl) {
    const number = canonicaliseMachineNumber(fromUrl[1]);
    // A `/machines/` URL whose number does not parse is a corrupt scan, not an
    // enrollment code — passing it on as one would produce a baffling
    // "code not found" instead of "try scanning again".
    return number ? {kind: 'machine', machineNumber: number} : null;
  }

  if (/\/enroll\//i.test(trimmed)) {
    return {kind: 'enrollment', pairingCode: parsePairingCode(trimmed)};
  }

  const typed = canonicaliseMachineNumber(trimmed);
  if (typed) {
    return {kind: 'machine', machineNumber: typed};
  }

  return {kind: 'enrollment', pairingCode: parsePairingCode(trimmed)};
}
